import { useGetDataLayer } from '@/services/adapters/useGetDataLayer'
import { useGetDataLayers } from '@/services/adapters/useGetDataLayers'
import { useGetLayerPacks } from '@/services/adapters/useGetLayerPacks'
import {
  IBaseDataLayer,
  IBaseDataLayerReference,
  IDataLayer,
  IDataLayerReference
} from '@lifk/types'
import { computed, ComputedRef, inject, InjectionKey, provide, ref } from 'vue'

type TFullDataLayersStore = {
  fullDataLayers: ComputedRef<IDataLayer[]>
  dataLayerReference: ComputedRef<IDataLayerReference | undefined>
  dataLayersByPack: ComputedRef<Record<string, IDataLayer[]>>
  addHiddenLayers: (keys: string[]) => void
}
export const FullDataLayersKey = Symbol() as InjectionKey<TFullDataLayersStore>

export const provideFullDataLayers = () => {
  const { getGuidebookDataLayer, fetchDataLayerByKey } = useGetDataLayer()
  const { data: dataLayer } = getGuidebookDataLayer()
  const { data: dataLayers } = useGetDataLayers()
  const { data: layerPacks } = useGetLayerPacks()

  // optionally fetch hidden data layers to add to the fullDataLayers array
  const hiddenLayersToFetch = ref<string[]>([])
  const hiddenDataLayers = ref<IBaseDataLayer[]>([])

  const allDataLayers = computed(() => {
    const libraryLayers = dataLayers.value ?? []
    // there is a race condition that may cause layers to be added to both collections.
    // Filter out duplicates
    const hiddenToAdd = hiddenDataLayers.value.filter(
      (dl) => !libraryLayers.some((ll) => ll.key === dl.key)
    )
    return [...libraryLayers, ...hiddenToAdd]
  })

  const addHiddenLayers = (keys: string[]) => {
    keys.forEach(async (key) => {
      if (
        hiddenLayersToFetch.value.includes(key) ||
        allDataLayers.value.some((dl) => dl.key === key)
      )
        return

      hiddenLayersToFetch.value.push(key)
      try {
        const layer = await fetchDataLayerByKey(key)
        if (layer) hiddenDataLayers.value.push(layer)
      } catch (_error) {
        // silently fail and continue loading others
        // (error will already be sent to monitoring within the data store)
        return
      }
    })
  }

  const getDataLayerWithAssociations = (
    dataLayer: IBaseDataLayer | IBaseDataLayerReference
  ): IDataLayer | IDataLayerReference => {
    const associatedLPs = layerPacks.value?.filter((lp) =>
      lp.dataLayers.includes(dataLayer.key)
    )
    return {
      ...dataLayer,
      layerPacks: associatedLPs ?? []
    }
  }

  const fullDataLayers: ComputedRef<IDataLayer[]> = computed(() =>
    allDataLayers.value.map(
      (dataLayer) => getDataLayerWithAssociations(dataLayer) as IDataLayer
    )
  )

  const dataLayerReference: ComputedRef<IDataLayerReference | undefined> =
    computed(() => {
      if (!dataLayer.value) return
      return getDataLayerWithAssociations(
        dataLayer.value
      ) as IDataLayerReference
    })

  // For quick data layer lookup by Layer Pack _id
  const dataLayersByPack: ComputedRef<Record<string, IDataLayer[]>> = computed(
    () =>
      (layerPacks.value ?? []).reduce(
        (acc: Record<string, IDataLayer[]>, layerPack) => {
          // Keep layer pack data layer ordering in tact
          acc[layerPack._id] = layerPack.dataLayers
            .map((layerKey) =>
              fullDataLayers.value.find(({ key }) => layerKey === key)
            )
            .filter((dl) => !!dl)
          return acc
        },
        {}
      )
  )

  provide(FullDataLayersKey, {
    fullDataLayers,
    dataLayerReference,
    dataLayersByPack,
    addHiddenLayers
  })
}

export default function useFullDataLayers(): TFullDataLayersStore {
  return inject(FullDataLayersKey)!
}
